import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Typography,
  MenuItem,
  CircularProgress
} from "@mui/material";
import { authenticationService } from "src/services";
import { get, put } from "src/services/httpClient.service";
import { useSnackbar } from "src/components/snackbar/useSnackbar";

/* =========================
   Types
========================= */
interface State {
  id: number;
  name: string;
}

interface UpdateCityProps {
  handleClose: () => void;
  fetchCities: () => void;
  Cityidvalues: any;
}

const UpdateCity: React.FC<UpdateCityProps> = ({
  handleClose,
  fetchCities,
  Cityidvalues
}) => {
  const { vendorId } = authenticationService.getVendorFranchiseIds();
  const APIURL = process.env.REACT_APP_BASE_API_URL;
  const { showSnackbar } = useSnackbar();

  const [districts, setDistricts] = useState<State[]>([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    id: 0,
    districtId: "",
    city: "",
    zipCode:"",
    shortCode: "",
    isActive: true,
    vendorId: vendorId
  });

  /* =========================
     Fetch States (GET)
  ========================= */
  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const res = await get(
          `${APIURL}/api/miscellaneous/district/vendor/${vendorId}/findAll`
        );
        setDistricts(res?.data || []);
      } catch (error) {
        showSnackbar("Failed to load districts", "error");
      }
    };

    fetchDistricts();
  }, [APIURL, vendorId, showSnackbar]);

  /* =========================
     Prefill City Data
  ========================= */
  useEffect(() => {
    if (Cityidvalues) {
      setForm({
        id: Cityidvalues.id,
        districtId: Cityidvalues.districtId,
        city: Cityidvalues.city || "",
        zipCode: Cityidvalues.zipCode || "",
        shortCode: Cityidvalues.shortCode || "",
        isActive: Cityidvalues.isActive,
        vendorId: vendorId
      });
    }
  }, [Cityidvalues, vendorId]);

  /* =========================
     Handlers
  ========================= */
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!form.districtId || !form.city) {
      showSnackbar("State and City Name are required", "warning");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        id: form.id,
        districtId: Number(form.districtId),
        city: form.city,
        zipCode: form.zipCode,
        shortCode: form.shortCode || null,
        isActive: form.isActive,
        vendorId: form.vendorId
      };

      const response = await put(
        `${APIURL}/api/miscellaneous/city`,
        payload
      );

      if (response?.statusCode === 200) {
        showSnackbar(
          response?.message || "City updated successfully",
          "success"
        );
        fetchCities();
        handleClose();
      } else {
        showSnackbar(
          response?.message || "Failed to update city",
          "error"
        );
      }
    } catch (error: any) {
      showSnackbar(error?.message || "Failed to update city", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box p={3}>
      <Typography variant="h6" mb={2}>
        Update City
      </Typography>

      <Grid container spacing={2}>
        {/* State Dropdown */}
        <Grid item xs={12}>
          <TextField
            select
            fullWidth
            required
            label="District"
            name="districtId"
            value={form.districtId}
            onChange={handleChange}
          >
            {districts.map(districts => (
              <MenuItem key={districts.id} value={districts.id}>
                {districts.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* City Name */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label="City Name"
            name="city"
            value={form.city}
            onChange={handleChange}
          />
        </Grid>

        {/* Short Code */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Short Code (Optional)"
            name="shortCode"
            value={form.shortCode}
            onChange={handleChange}
          />
        </Grid>

        
        {/* zipCode */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="zipCode (Optional)"
            name="zipCode"
            value={form.zipCode}
            onChange={handleChange}
          />
        </Grid>

        {/* Active Switch */}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={form.isActive}
                onChange={e =>
                  setForm(prev => ({
                    ...prev,
                    isActive: e.target.checked
                  }))
                }
              />
            }
            label="Is Active"
          />
        </Grid>

        {/* Actions */}
        <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2}>
          <Button variant="outlined" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={
              loading ? <CircularProgress size={20} color="inherit" /> : null
            }
          >
           {loading ? "Updating..." : "Update"} 
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UpdateCity;
